import { config } from "./config";

class ApiClient {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  async request(method, path, body = null) {
    const headers = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = this.token;
    }

    const options = {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    };

    const response = await fetch(`${config.apiUrl}${path}`, options);

    if (!response.ok) {
      // Handle expired token - redirect to login
      if (response.status === 401) {
        window.location.href = "/";
        throw new Error("Session expired. Please log in again.");
      }

      const error = await response.json().catch(() => ({ message: "Request failed" }));
      // Try both 'error' and 'message' fields from the API response
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Feeds
  async getFeeds() {
    return this.request("GET", "/feeds");
  }

  async createFeed(feed) {
    return this.request("POST", "/feeds", feed);
  }

  async updateFeed(feedId, feed) {
    return this.request("PUT", `/feeds/${feedId}`, feed);
  }

  async deleteFeed(feedId) {
    return this.request("DELETE", `/feeds/${feedId}`);
  }

  async regenerateRss(feedId) {
    return this.request("POST", `/feeds/${feedId}/regenerate`);
  }

  async testSlackWebhook(feedId) {
    return this.request("POST", `/feeds/${feedId}/test-slack`);
  }

  // Admin
  async getAllFeeds() {
    return this.request("GET", "/admin/feeds");
  }

  async getAdmins() {
    return this.request("GET", "/admin/admins");
  }

  async addAdmin(email) {
    return this.request("POST", "/admin/admins", { email });
  }

  async removeAdmin(email) {
    return this.request("DELETE", `/admin/admins/${encodeURIComponent(email)}`);
  }

  async getUserStats() {
    return this.request("GET", "/admin/users");
  }

  async getLegacyWebhookStats() {
    return this.request("GET", "/admin/legacy-webhooks");
  }

  async updateLegacyWebhook(webhookId, updates) {
    return this.request("PUT", `/admin/legacy-webhooks/${encodeURIComponent(webhookId)}`, updates);
  }

  // Metadata
  async getRegions() {
    return this.request("GET", "/regions");
  }

  async getServices() {
    return this.request("GET", "/services");
  }

  // Service Management (Admin)
  async listServicesAdmin() {
    return this.request("GET", "/admin/services");
  }

  async createService(service) {
    return this.request("POST", "/admin/services", service);
  }

  async updateService(serviceName, updates) {
    return this.request("PUT", `/admin/services/${encodeURIComponent(serviceName)}`, updates);
  }

  async deleteService(serviceName) {
    return this.request("DELETE", `/admin/services/${encodeURIComponent(serviceName)}`);
  }

  // Source Feeds Management (Admin)
  async listSourceFeeds() {
    return this.request("GET", "/admin/source-feeds");
  }

  // Source Feeds (public - for custom feed creation)
  async getSourceFeeds() {
    return this.request("GET", "/source-feeds");
  }

  async createSourceFeed(feed) {
    return this.request("POST", "/admin/source-feeds", feed);
  }

  async updateSourceFeed(feedId, updates) {
    return this.request("PUT", `/admin/source-feeds/${feedId}`, updates);
  }

  async deleteSourceFeed(feedId) {
    return this.request("DELETE", `/admin/source-feeds/${feedId}`);
  }

  // System Actions (Admin)
  async triggerFeedProcessor() {
    return this.request("POST", "/admin/trigger-feed-processor");
  }

  async triggerSlackNotifier() {
    return this.request("POST", "/admin/trigger-slack-notifier");
  }
}

export const api = new ApiClient();
